import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Database, Download } from "lucide-react";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { toast } from "sonner";

const ENDPOINT_MAP = {
  wage_sheet:     "/admin/wage-sheet/upload",
  room_count:     "/admin/room-count/upload",
  rent_master:    "/admin/rent-master/upload",
  general_ledger: "/admin/general-ledger/upload",
};

const ICON_MAP = {
  wage_sheet:     "#FB7185",
  room_count:     "#20B2AA",
  rent_master:    "#A78BFA",
  general_ledger: "#6366F1",
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n > 1e6) return `${(n / 1e6).toFixed(2)} MB`;
  if (n > 1e3) return `${(n / 1e3).toFixed(1)} KB`;
  return `${n} B`;
};

export default function DataSourcesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const refs = useRef({});

  const [reloadKey, setReloadKey] = useState(0);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-sources");
      setData(data);
    } catch (e) {
      logError("data-sources load failed", e);
      toast.error("Could not load data sources");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reloadKey]);

  const onUploadHandler = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xlsx|xlsm|xls)$/)) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    setUploadingKey(key);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(ENDPOINT_MAP[key], form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded${data.stats ? ` · ${JSON.stringify(data.stats)}` : ""}`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      logError(`upload ${key} failed`, err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey(null);
      if (refs.current[key]) refs.current[key].value = ""; // reset so same-name file can re-upload
    }
  };

  if (loading || !data) return (
    <div className="py-16 text-center text-zinc-500">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-data-sources">
      <header>
        <h2 className="font-display font-black text-2xl tracking-tight">Reference Data Sources</h2>
        <p className="text-[12px] text-zinc-500 mt-0.5">
          Upload updated <span className="font-bold">Wage Sheet</span>, <span className="font-bold">Room Count</span>, and (soon) <span className="font-bold">General Ledger</span> Excel files.
          Changes propagate to MIS Analysis dashboards instantly.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.sources.map((s) => {
          const isPlaceholder = s.key === "general_ledger" && !s.file_present;
          return (
            <div key={s.key} className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col" data-testid={`data-source-${s.key}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${ICON_MAP[s.key] || "#94a3b8"}22` }}>
                    {s.key === "general_ledger" ? <Database className="w-4 h-4" style={{ color: ICON_MAP[s.key] }} /> : <FileSpreadsheet className="w-4 h-4" style={{ color: ICON_MAP[s.key] }} />}
                  </div>
                  <div>
                    <div className="font-display font-black text-base text-zinc-900">{s.label}</div>
                    <div className="text-[10.5px] text-zinc-500">{s.description}</div>
                  </div>
                </div>
                {s.file_present ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" title="File loaded" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500" title="No file uploaded yet" />
                )}
              </div>

              <div className="space-y-1 text-[11px] text-zinc-600 mt-1 flex-1">
                <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">Size</span><span className="tabular-nums">{fmtBytes(s.size_bytes)}</span></div>
                {s.filename && (
                  <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">Filename</span><span className="truncate ml-2 max-w-[140px]" title={s.filename}>{s.filename}</span></div>
                )}
                {s.uploaded_at && (
                  <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">Last upload</span><span className="text-[10.5px]">{new Date(s.uploaded_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span></div>
                )}
                {s.uploaded_by && (
                  <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">By</span><span className="truncate ml-2 max-w-[140px]" title={s.uploaded_by}>{s.uploaded_by}</span></div>
                )}
                {s.stats?.months && (
                  <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">Months</span><span className="tabular-nums">{s.stats.months}</span></div>
                )}
                {s.stats?.locations && (
                  <div className="flex justify-between"><span className="text-zinc-400 uppercase tracking-wider text-[9.5px]">Locations</span><span className="tabular-nums">{s.stats.locations}</span></div>
                )}
                {s.status && (
                  <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 inline-block px-1.5 py-0.5 rounded">{s.status}</div>
                )}
              </div>

              {/* Upload action */}
              <input
                type="file"
                accept=".xlsx,.xlsm"
                ref={(el) => { refs.current[s.key] = el; }}
                onChange={onUploadHandler(s.key)}
                className="hidden"
                data-testid={`data-source-input-${s.key}`}
              />
              <button
                onClick={() => refs.current[s.key]?.click()}
                disabled={uploadingKey === s.key}
                data-testid={`data-source-upload-${s.key}`}
                className={`mt-3 w-full py-2 rounded font-bold text-[12px] inline-flex items-center justify-center gap-1.5 transition-colors ${
                  uploadingKey === s.key
                    ? "bg-zinc-200 text-zinc-500"
                    : isPlaceholder
                      ? "bg-indigo-700 text-white hover:bg-indigo-800"
                      : "bg-teal-700 text-white hover:bg-teal-800"
                }`}
              >
                {uploadingKey === s.key ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="w-3.5 h-3.5" /> {s.file_present ? "Replace file" : "Upload .xlsx"}</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="text-[10.5px] text-zinc-500 flex items-start gap-1.5 max-w-3xl">
        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Uploads are admin-only and audited. Previous files are backed up to <code className="bg-zinc-100 px-1 rounded">backend/data/&lt;name&gt;_YYYYMMDD_HHMMSS.xlsx.bak</code>.
          General Ledger parsing will be implemented once the schema is shared.
        </span>
      </div>

      {/* Migrate-from-preview block — only renders something meaningful when a
          backup tarball is bundled into this deploy. Idempotent restore: safely
          skips collections that already have data. */}
      <PreviewBackupRestore />
    </div>
  );
}

function PreviewBackupRestore() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/backup-status");
      setStatus(data);
    } catch (e) {
      logError("backup-status load failed", e);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const doDownloadDump = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const resp = await api.post("/admin/dump-current-db", null, { responseType: "blob" });
      const filename = (resp.headers?.["x-backup-filename"]) ||
                       `db-snapshot-${new Date().toISOString().replace(/[:.]/g, "-")}.tar.gz`;
      let countsSummary = "";
      try {
        const counts = JSON.parse(resp.headers?.["x-collection-counts"] || "{}");
        const total = Object.values(counts).reduce((a, b) => a + (b > 0 ? b : 0), 0);
        countsSummary = ` · ${Object.keys(counts).length} collections · ${total} docs`;
      } catch { /* header missing/malformed — leave summary empty */ }
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/gzip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}${countsSummary}`);
    } catch (e) {
      logError("db dump download failed", e);
      // Because responseType is "blob", axios wraps 4xx/5xx JSON error bodies in a Blob.
      // Read the blob back as text and try to extract the FastAPI `detail` so the toast
      // surfaces the real backend error (mongodump missing, TLS error, timeout, etc.)
      // instead of the generic fallback.
      let detail = e.response?.data?.detail || null;
      if (!detail && e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt)?.detail || txt.slice(0, 400); }
          catch { detail = txt.slice(0, 400); }
        } catch { /* blob read failed */ }
      }
      // Special-case: 401/403 means the session lapsed. Tell the user to re-login
      // (their next click otherwise silently repeats the same error).
      if (e.response?.status === 401) {
        toast.error("Session expired — please sign out and sign in again, then retry.");
      } else if (e.response?.status === 403) {
        toast.error(detail || "Admin permission required — you are signed in but not as an admin.");
      } else {
        toast.error(detail || `DB dump failed (HTTP ${e.response?.status ?? "?"})`);
      }
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async ({ dry = false, overwrite = false } = {}) => {
    if (busy) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (dry) params.set("dry_run", "true");
      else params.set("confirm", overwrite ? "OVERWRITE" : "RESTORE");
      if (overwrite) params.set("overwrite", "true");
      const { data } = await api.post(`/admin/restore-from-backup?${params.toString()}`);
      setResult(data);
      if (!dry) {
        const s = data.summary || {};
        const total = (s.restored || 0) + (s.replaced || 0);
        const tone = total > 0 ? "success" : "warning";
        const msg = overwrite
          ? `Force-replace complete · ${s.replaced || 0} collections replaced · ${s.restored || 0} fresh · ${s.skipped || 0} protected`
          : `Restore complete · ${s.restored || 0} imported · ${s.skipped || 0} skipped (already had data)`;
        if (tone === "success") toast.success(msg);
        else toast.warning(msg);
        await loadStatus();
      }
    } catch (e) {
      logError("restore failed", e);
      if (e.response?.status === 401) {
        toast.error("Session expired — please sign out and sign in again, then retry.");
      } else if (e.response?.status === 403) {
        toast.error(e.response?.data?.detail || "Admin permission required — you are signed in but not as an admin.");
      } else {
        toast.error(e.response?.data?.detail || `Restore failed (HTTP ${e.response?.status ?? "?"})`);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  if (!status.available) {
    return (
      <div className="mt-6 border border-zinc-200 rounded-lg p-4 bg-zinc-50/50">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Migrate from Preview</div>
        <div className="text-sm text-zinc-600">No preview-backup tarball bundled in this deploy — nothing to restore.</div>
      </div>
    );
  }

  const counts = status.current_collection_counts || {};
  const isEmpty = Object.values(counts).every((c) => !c);

  return (
    <div className="mt-6 border-2 border-dashed border-indigo-200 rounded-lg p-4 bg-indigo-50/30" data-testid="preview-backup-restore">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">Migrate from Preview</div>
          <h3 className="font-display font-bold text-base mt-0.5">Restore preview data into this environment</h3>
          <div className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Bundled backup: <code className="bg-white px-1 rounded">{status.bundle_name}</code> · {fmtBytes(status.bundle_size_bytes)}
            {status.bundle_modified_at && (
              <span> · bundled {new Date(status.bundle_modified_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
            )}.
            <br/>
            <span className="text-zinc-700">Two modes:</span> <span className="font-bold text-indigo-700">Restore missing</span> seeds empty collections only (safe).
            <span className="font-bold text-rose-700"> Force replace</span> drops the prod copy of every collection in the bundle and rewrites it from preview — use when prod is stale and preview is the source of truth. User sessions + storage-meta are always protected.
          </div>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            data-testid="dump-current-db"
            onClick={doDownloadDump}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Runs mongodump against THIS environment's DB and downloads it as a .tar.gz. Call this on PRODUCTION before force-replacing to keep an escape hatch."
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download this env's DB
          </button>
          <button
            data-testid="restore-dry-run"
            onClick={() => doRestore({ dry: true })}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-xs font-mono-tech uppercase tracking-wider bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Dry-run (safe)"}
          </button>
          <button
            data-testid="restore-confirm"
            onClick={() => {
              if (window.confirm("Restore preview data into this environment? Collections that already have data will be SKIPPED — your existing prod data is safe. Continue?")) {
                doRestore({ dry: false, overwrite: false });
              }
            }}
            disabled={busy}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${
              isEmpty ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            } disabled:opacity-50`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (isEmpty ? "Restore (env is empty)" : "Restore missing only")}
          </button>
          <button
            data-testid="restore-force"
            onClick={() => {
              const msg = "FORCE REPLACE will DROP every collection in this environment that exists in the preview backup, then rewrite it from preview.\n\n" +
                          "ALL IC submissions, properties, users, etc. in THIS environment will be REPLACED by the preview copy. This cannot be undone.\n\n" +
                          "Type OK to confirm.";
              const reply = window.prompt(msg, "");
              if (reply && reply.trim().toUpperCase() === "OK") {
                doRestore({ dry: false, overwrite: true });
              }
            }}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Force replace · preview wins"}
          </button>
        </div>
      </div>

      {/* Current row counts so the admin can see what will be skipped */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 text-[10px] mb-3">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="bg-white border border-zinc-200 rounded px-2 py-1.5">
            <div className="font-mono-tech uppercase tracking-wider text-zinc-500 text-[9px]">{k}</div>
            <div className="font-bold tabular-nums">{v ?? 0}</div>
          </div>
        ))}
      </div>

      {result && (
        <div className="mt-3 bg-white border border-indigo-200 rounded p-2.5 text-[11px]" data-testid="restore-result">
          <div className="font-mono-tech uppercase tracking-wider text-[9.5px] text-zinc-500 mb-1.5">
            {result.dry_run ? "DRY-RUN RESULT" : "RESTORE RESULT"}
            {result.overwrite ? " · FORCE REPLACE" : ""}
            {" · restored "}{result.summary.restored || 0}
            {result.summary.replaced ? ` · replaced ${result.summary.replaced}` : ""}
            {" · skipped "}{result.summary.skipped || 0}
            {" · errors "}{result.summary.errors || 0}
          </div>
          <div className="space-y-0.5">
            {(result.actions || []).map((a) => (
              <div key={a.collection} className="flex items-center gap-2 text-[10.5px]">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  a.status === "restored" ? "bg-emerald-500" :
                  a.status === "replaced" ? "bg-indigo-600" :
                  a.status === "WOULD RESTORE" || a.status === "WOULD REPLACE" ? "bg-indigo-400" :
                  a.status === "ERROR" ? "bg-rose-500" : "bg-zinc-300"
                }`} />
                <code className="bg-zinc-50 px-1 rounded text-[10px]">{a.collection}</code>
                <span className="text-zinc-600">{a.status}</span>
                {a.previous_rows != null && <span className="text-zinc-500">(was {a.previous_rows})</span>}
                {a.rows_imported != null && <span className="text-emerald-700 font-bold">+{a.rows_imported} rows</span>}
                {a.error && <span className="text-rose-700 truncate">{a.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
